import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Flex } from '@adobe/react-spectrum';

import { BaseDeckPane } from './BaseDeckPane';
import { Funnel, FunnelChart, ResponsiveContainer, Tooltip } from 'recharts';
import { getGrades } from '../../../data/ScoreDisplayUtils';
import {
    CHART_ACCENT_GOLD,
    CHART_ACCENT_RED,
    intensityHue,
    SEARED_TOOLTIP_CURSOR,
    SearedTooltip,
} from '../../common/cardStats/chartTheme.jsx';
import { SaltBreakdown } from './SaltBreakdown';
import './saltPane.css';

const CATEGORY_CONSTANTS = {
    annihilator: { label: 'Annihilator', chartLabel: 'Annihilator' },
    boardwipes: { label: 'Board Wipes', chartLabel: 'Board Wipes' },
    cant: { label: `You can't...`, chartLabel: `Uh… you can't do that` },
    mld: { label: 'MLD', chartLabel: 'Mass Land Denial' },
    taxes: { label: 'Taxes', chartLabel: 'Tax effects' },
    payTheOne: { label: 'Pay the…?', chartLabel: 'Do you pay the…?' },
    cardPrice: { label: 'Card Price', chartLabel: 'Card Price' },
    cedhMetashare: { label: 'cEDH Staples', chartLabel: 'cEDH Staples', noChart: true },
    sacrifice: { label: 'Sacrifice', chartLabel: 'Sacrifice' },
    infiniteCombos: { label: 'Infinite Combos', chartLabel: 'Infinite combos' },
    reserved: { label: 'Reserved List', chartLabel: 'Reserved List cards', noChart: true },
    edhrec: { label: 'EDHRec Scores', chartLabel: 'EDHRec Scores', noChart: true },
    locks: { label: 'Locks', chartLabel: 'Locks' },
    theft: { label: 'Theft', chartLabel: 'Theft' },
    chaos: { label: 'Chaos', chartLabel: 'Chaos effects' },
    groupSlug: { label: 'Group Slug', chartLabel: 'Group Slug' },
    extraTurns: { label: 'Extra Turns', chartLabel: 'Extra Turns' },
    extraCombats: { label: 'Extra Combats', chartLabel: 'Extra Combats' },
    poison: { label: 'Poison', chartLabel: 'Poison' },
    wheels: { label: 'Wheels', chartLabel: 'Wheels' },
    discard: { label: 'Discard', chartLabel: 'Discard' },
    offColorFetches: { label: 'Fetches', chartLabel: 'Off Color Fetches' },
};

const SUBCATEGORY_CONSTANTS = {
    cant: {
        cantActivate: { label: 'Activate', chartLabel: `Can't Activate` },
        cantAttack: { label: 'Attack', chartLabel: `Can't Attack` },
        cantCast: { label: 'Cast', chartLabel: `Can't Cast` },
        cantDraw: { label: 'Draw', chartLabel: `Can't Draw` },
        cantEven: { label: 'Even', chartLabel: `Can't Even` },
        cantGainLife: { label: 'Gain Life', chartLabel: `Can't Gain Life` },
        cantSearch: { label: 'Search', chartLabel: `Can't Search` },
        cantTrigger: { label: 'Trigger', chartLabel: `Can't Trigger` },
        cantUntap: { label: 'Untap', chartLabel: `Can't Untap` },
    },
};

export function SaltPane() {
    const deck = useSelector((state) => state.deck.deck);
    const cards = useSelector((state) => state.deck.cards);

    const getCategories = () => {
        const list = {};
        const categoryKeys = Object.keys(CATEGORY_CONSTANTS).sort((a, b) =>
            CATEGORY_CONSTANTS[a].label.toLowerCase() < CATEGORY_CONSTANTS[b].label.toLowerCase() ? -1 : 1
        );

        categoryKeys.forEach((category) => {
            const has = deck?.details?.salt?.scoring?.hasOwnProperty(category);
            if (!has && category !== 'cant' && category !== 'edhrec') return;

            const item = deck?.details?.salt?.scoring?.[category];
            let items = Object.keys(item?.list || []).map((key) => {
                const entry = item.list[key];
                if (
                    category?.toLowerCase()?.includes('combos') ||
                    category?.toLowerCase()?.includes('locks')
                ) {
                    return {
                        ...entry,
                        name: entry?.name?.toString()?.replace(/,(?! )/g, '\n'),
                    };
                }
                return {
                    ...entry,
                    ...cards?.[key],
                };
            });

            if (category === 'edhrec') {
                // The legacy `.salt` field on a card is the raw EDHREC-only
                // number (unbounded, occasionally wildly different from the
                // backend's categorised value). Drive this list off the
                // per-card saltCategories.edhrec.score instead, and drop any
                // card whose edhrec score is zero/missing — otherwise every
                // card in the deck shows up with a misleading "EDHREC"
                // contribution even when edhRecSalt is 0.
                items = Object.keys(cards || {})
                    .map((id) => {
                        const card = cards?.[id];
                        const edhrecScore = Number(card?.saltCategories?.edhrec?.score || 0);
                        return { ...card, score: edhrecScore };
                    })
                    .filter((c) => c.isFrontFace && c.score > 0);
            }

            list[category] = {
                label: CATEGORY_CONSTANTS[category].label,
                id: category,
                count: items?.length || 0,
                list: items,
                color: '#FFFFFF',
                subCategories: {},
                chartLabel: CATEGORY_CONSTANTS[category].chartLabel,
            };

            if (category === 'cant') {
                Object.keys(SUBCATEGORY_CONSTANTS[category] || {}).forEach((subCategory) => {
                    if (!deck?.details?.salt?.scoring?.[subCategory]) return;
                    const subItem = deck?.details?.salt?.scoring?.[subCategory];
                    const subItems = subItem?.list || [];
                    list[category].subCategories[subCategory] = {
                        label: SUBCATEGORY_CONSTANTS[category][subCategory].label,
                        id: subCategory,
                        count: Object.keys(subItems).length,
                        list: Object.keys(subItems).map((_key) => ({
                            ...subItems[_key],
                            ...cards?.[_key],
                        })),
                        parentCategory: category,
                    };
                    list[category].count += Object.keys(subItems).length;
                    list[category].list = list[category].list.concat(
                        list[category].subCategories[subCategory].list
                    );
                });
            }
        });

        if (list['cant']?.count < 1) {
            delete list['cant'];
        }

        return list;
    };

    const categories = useMemo(() => getCategories(), [deck, cards]);

    // Each nail segment's color gradients from deep red (top, saltiest) to
    // gold/green (tip, chillest). Hue is keyed to the deck's overall
    // saltiness percentage so a high-salt deck stays mostly red.
    const chartData = useMemo(() => {
        const saltiness = getGrades(deck)?.salt?.percentage || 0;
        const rows = [];
        Object.keys(categories).forEach((category) => {
            if (CATEGORY_CONSTANTS?.[category]?.noChart) return;
            const item = categories[category];
            const items = item?.list || [];
            if (items.length === 0) return;
            const subTotal = items.reduce((acc, cur) => acc + parseFloat(cur?.score || 0), 0);
            rows.push({
                value: subTotal,
                label: item.chartLabel,
                name: item.chartLabel,
            });
        });

        rows.sort((a, b) => b.value - a.value);

        const stepRange = Math.max(20, Math.min(70, 100 - saltiness));
        const step = rows.length > 1 ? stepRange / (rows.length - 1) : 0;
        return rows.map((row, idx) => {
            // Wider bands on the top, narrower near the tip — saltiest
            // sits right at the head of the nail where it strikes.
            const hue = intensityHue(saltiness - idx * step);
            return {
                ...row,
                fill: `hsl(${hue} 85% 50%)`,
            };
        });
    }, [categories, deck]);

    const saltPercentageScore = getGrades(deck)?.salt?.percentage ?? 0;

    const endGameEmojis = useMemo(() => {
        if (saltPercentageScore > 90) return '🤬 🤬 🤬';
        if (saltPercentageScore > 80) return '😡 😡 😡';
        if (saltPercentageScore > 70) return '🤯 🤯 🤯';
        if (saltPercentageScore > 60) return '😤 😤 😤';
        if (saltPercentageScore > 50) return '😭 😭 😭';
        if (saltPercentageScore > 40) return '😢 😢 😢';
        if (saltPercentageScore > 30) return '🤨 🤨 🤨';
        if (saltPercentageScore > 20) return '😒 😒 😒';
        if (saltPercentageScore > 10) return '🥹 🥹 🥹';
        return '😃 😃 😃';
    }, [saltPercentageScore]);

    // Intensity bar above the chart — reads as the "head" of the nail
    // + an overall intensity meter keyed to the saltiness %.
    const intensityAngle = Math.max(0, Math.min(360, (saltPercentageScore / 100) * 360));

    return (
        <BaseDeckPane title="Salt" content={<div />} showScore={true} categories={categories}>
            <Flex direction="column" rowGap={16} marginTop={10}>
                <div className="saltNailPanel panelGradient">
                    <div className="saltNailHeader">
                        <span className="saltNailEyebrow">Saltiness Audit</span>
                        <span className="saltNailTitle">How are we going to lose friends today?</span>
                    </div>

                    <div className="saltNailIntensityRow">
                        <span className="saltNailIntensityLabel">Rage meter</span>
                        <div className="saltNailIntensityTrack">
                            <div
                                className="saltNailIntensityFill"
                                style={{
                                    width: `${saltPercentageScore}%`,
                                    background: `linear-gradient(90deg, ${CHART_ACCENT_GOLD} 0%, ${CHART_ACCENT_RED} 100%)`,
                                }}
                            />
                        </div>
                        <span className="saltNailIntensityValue">
                            {saltPercentageScore.toFixed(1)}%
                        </span>
                    </div>

                    {chartData.length > 0 && (
                        <div className="saltNailStage">
                            <div
                                className="saltNailStrike"
                                style={{
                                    background: `conic-gradient(from ${-intensityAngle / 2}deg at 50% 0%, rgba(204, 26, 26, 0.25) 0deg, rgba(204, 26, 26, 0.08) ${intensityAngle}deg, transparent ${intensityAngle + 20}deg)`,
                                }}
                            />
                            <div className="saltNailColumn">
                                <div className="saltNailHead">
                                    <span>CHEF&apos;S KISS</span>
                                    <img
                                        src="/resources/chef-kiss-header.png"
                                        alt=""
                                        width="28"
                                        height="28"
                                    />
                                </div>
                                <div className="saltNailFunnelWrap">
                                    <ResponsiveContainer
                                        width="100%"
                                        height={Math.max(220, chartData.length * 48)}
                                    >
                                        <FunnelChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                                            <Tooltip
                                                content={
                                                    <SearedTooltip
                                                        hideLabel
                                                        valueFormatter={(v, entry) => {
                                                            const payload = entry?.payload || {};
                                                            const score = Number.isFinite(payload?.value)
                                                                ? payload.value.toFixed(1)
                                                                : v;
                                                            return `${payload?.label || entry?.name || ''} · ${score}`;
                                                        }}
                                                    />
                                                }
                                                cursor={SEARED_TOOLTIP_CURSOR}
                                            />
                                            <Funnel
                                                dataKey="value"
                                                data={chartData}
                                                isAnimationActive
                                                stroke="rgba(0, 0, 0, 0.6)"
                                                strokeWidth={1}
                                            />
                                        </FunnelChart>
                                    </ResponsiveContainer>
                                    <div className="saltNailDrivenShadow" />
                                </div>
                                <div className="saltNailEmojis">
                                    <img
                                        src="/resources/chef-kiss-header.png"
                                        alt=""
                                        className="saltNailKissIcon"
                                    />
                                    <span>{endGameEmojis}</span>
                                </div>
                            </div>
                            <div className="saltNailLegend">
                                {chartData.map((row) => (
                                    <div
                                        key={`legend_${row.label}`}
                                        className="saltNailLegendRow"
                                        style={{ borderLeftColor: row.fill }}
                                    >
                                        <span className="saltNailLegendLabel">{row.label}</span>
                                        <span className="saltNailLegendValue">
                                            {row.value.toFixed(1)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="saltNailFooter">
                        <span className="saltNailPercent">{saltPercentageScore.toFixed(3)}%</span>
                        <span className="saltNailFooterText">
                            chance of enraging your play group.
                        </span>
                    </div>
                </div>

                <SaltBreakdown
                    categories={categories}
                    deckTotal={deck?.details?.salt?.total}
                />
            </Flex>
        </BaseDeckPane>
    );
}
